import { useCallback } from 'react';
import { noop } from 'lodash';
import type { ApolloCache } from '@frameio/graphql-client';
import { updateQuery } from '@frameio/graphql-client';
import type {
  Account,
  CreateProjectFromTemplateInput,
  CreateProjectFromTemplateMutationResult,
  GetImageUploadQueryResult,
  Project,
} from '@frameio/graphql-client/generated';
import {
  GetAccountProjects,
  GetWorkspaceProjects,
  useCreateProjectFromTemplateMutation,
  useGetImageUploadLazyQuery,
} from '@frameio/graphql-client/generated';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { useTrackUnsafe } from '../../../segment/hooks';
import { WORKSPACE_PROJECTS_PAGE_SIZE } from '../../constants';
import { MOUNTED_STORAGE_ACTIVITY_WINDOW_DAYS } from '../../mounted-storage/constants';
import { useLoadingToast } from '../../shared';
import { incrementProjectCountForWorkspaceInfoBasic } from '../cache-ops';
import { uploadProjectImage } from '../utils';
import { getProjectVisibilityEnum } from '../utils/getProjectVisibilityEnum';
import TEXT from './generated/TEXT/Shared_UseCreateProjectFromTemplate';

export type CreatedProjectFromTemplateResult = NonNullable<
  NonNullable<
    CreateProjectFromTemplateMutationResult['data']
  >['createProjectFromTemplate']
>['project'];

type CreateProjectFromTemplateOpts = {
  onError?: () => void;
  onSuccess?: (project: CreatedProjectFromTemplateResult) => void;
};

type CreateProjectFromTemplateReturn = (
  details: CreateProjectFromTemplateInput,
  accountId: Account['id'],
  blob?: Blob | null
) => void;

const DEFAULT_OPTS: CreateProjectFromTemplateOpts = {
  onError: noop,
  onSuccess: noop,
};

/**
 * The useCreateProjectFromTemplate hook can be used to create a project with a template. Adding an avatar
 * image requires providing an imageUploadUrl and a blob for the avatar.
 *
 * @param opts.onError an optional callback fired if the create fails
 * @param opts.onSuccess an optional callback fired if the create succeeds
 * @returns a function to call when ready to create the project
 */
export function useCreateProjectFromTemplate(
  opts?: CreateProjectFromTemplateOpts
): CreateProjectFromTemplateReturn {
  const { onError, onSuccess } = {
    ...DEFAULT_OPTS,
    ...opts,
  };
  const [createProject] = useCreateProjectFromTemplateMutation();
  const [imageUploadQuery] = useGetImageUploadLazyQuery({
    fetchPolicy: 'network-only',
  });
  const { addToast, updateAndHideToast } = useLoadingToast();
  const track = useTrackUnsafe();

  return useCallback(
    /**
     * @param details the CreateProjectFromTemplateInput values including destinationWorkspaceId, name,
     *              templateId and image (optional)
     * @param accountId the current user's active account ID
     * @param blob Blob instance for the avatar
     */
    async (details, accountId, blob = undefined) => {
      const input = {
        ...details,
      };
      let imageUploadResult: GetImageUploadQueryResult;
      let imageUploadUrl: string;
      const toastId = addToast(TEXT.loading);

      track(
        'project-created',
        {
          project_template_id: input.templateId,
          project_visibility: getProjectVisibilityEnum(input.private),
          source: 'Home',
        },
        { logToDatadog: true }
      );

      if (blob) {
        imageUploadResult = await imageUploadQuery({
          variables: {
            mimeType: blob.type,
            resourceType: 'PROJECT',
          },
        });

        if (imageUploadResult?.data?.imageUpload?.url) {
          imageUploadUrl = imageUploadResult.data.imageUpload.url;
          input.image = {
            id: imageUploadResult.data.imageUpload.id,
            type: blob.type,
          };

          uploadProjectImage(imageUploadUrl, blob, noop);
        }
      }

      const handleReject = () => {
        updateAndHideToast(toastId, {
          title: TEXT.errorMessage,
          variant: 'error',
        });
        onError?.();
      };

      const handleResolve = (
        result: CreateProjectFromTemplateMutationResult
      ) => {
        if (result.data?.createProjectFromTemplate) {
          updateAndHideToast(toastId, {
            title: TEXT.successMessage,
            variant: 'success',
          });
          const project = result.data?.createProjectFromTemplate.project;
          onSuccess?.(project);
        } else {
          handleReject();
        }
      };

      createProject({
        variables: {
          input: {
            ...input,
          },
        },
        update(cache: ApolloCache<any>, result) {
          const newProject = result?.data?.createProjectFromTemplate?.project;

          if (!newProject) return;

          incrementProjectCountForWorkspaceInfoBasic({
            cache,
            destinationWorkspaceId: input.destinationWorkspaceId,
            newProject: newProject as Project,
            accountId,
          });

          updateQuery(
            cache,
            GetWorkspaceProjects,
            {
              workspaceId: input.destinationWorkspaceId,
              page: {
                first: WORKSPACE_PROJECTS_PAGE_SIZE,
              },
            },
            (draft) => {
              if (!draft) return;

              draft.account?.workspace?.projects?.nodes.push(newProject);
            }
          );

          updateQuery(
            cache,
            GetAccountProjects,
            {
              accountId,
              page: {
                first: DEFAULT_INITIAL_PAGE_SIZE,
              },
              mountedStorageActivityDays: MOUNTED_STORAGE_ACTIVITY_WINDOW_DAYS,
            },
            (draft) => {
              if (!draft?.account?.projects) return;
              draft.account.projects.nodes.unshift(newProject);
              draft.account.projects.totalCount += 1;
            }
          );
        },
      }).then(handleResolve, handleReject);
    },
    [
      addToast,
      track,
      createProject,
      imageUploadQuery,
      updateAndHideToast,
      onError,
      onSuccess,
    ]
  );
}

TEXT.DEFINE({
  /** Toast message while creating project */
  loading: 'Creating Project',
  /** Toast message when project creation fails */
  errorMessage: "Couldn't create Project",
  /** Toast message when project is successfully created */
  successMessage: 'Project created',
});
